import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import type { Article } from "@/types/article";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileText } from "lucide-react";
import { ChevronLeft } from "lucide-react";

const statusOptions = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "review", label: "In Review" }
];

const placeholderImages = [
  "https://images.unsplash.com/photo-1649972904349-6e44c42644a7",
  "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
  "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
  "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
];

const CreateArticleForm = () => {
  const [article, setArticle] = useState<Partial<Article>>({
    title: "",
    description: "",
    category: "",
    status: "draft",
    premium: false,
    image: placeholderImages[3],
    readTime: "5 min read"
  });
  const [saving, setSaving] = useState(false);
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
  const navigate = useNavigate();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!article.title) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .insert({
          title: article.title,
          description: article.description,
          category: article.category,
          status: article.status,
          premium: article.premium,
          image: article.image,
          read_time: article.readTime,
          date: new Date().toISOString()
        })
        .select();
      
      if (error) {
        console.log("Insert error:", error);
        toast.error("Failed to create article");
      } else {
        toast.success("Article created successfully");
        navigate("/admin");
      }
    } catch (err) {
      console.error("Error creating article:", err);
      toast.error("Failed to create article");
    } finally {
      setSaving(false);
    }
  };

  const selectPlaceholderImage = (url: string) => {
    setArticle({ ...article, image: url });
  };

  return (
    <div className="w-full px-4 md:px-8 lg:px-16 xl:px-24">
      <div className="mb-6 flex items-center">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 px-2 py-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Admin Dashboard
        </Button>
      </div>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Create New Article</CardTitle>
        </CardHeader>
        <form onSubmit={handleSave}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Enter article title"
                value={article.title || ""}
                onChange={e => setArticle({ ...article, title: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="description">Content (Markdown supported)</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                  className="flex items-center gap-1 text-xs"
                >
                  <FileText className="h-4 w-4" />
                  {showMarkdownPreview ? "Edit" : "Preview"}
                </Button>
              </div>
              <Textarea
                id="description"
                placeholder="Write your article content here... Markdown is supported"
                className="min-h-[250px] font-mono"
                value={article.description || ""}
                onChange={e => setArticle({ ...article, description: e.target.value })}
              />
              {showMarkdownPreview && article.description && (
                <div className="mt-2 p-4 border rounded-md bg-gray-50">
                  <div dangerouslySetInnerHTML={{ __html: article.description }} />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="image">Header Image</Label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {placeholderImages.map((img, i) => (
                  <div 
                    key={i} 
                    className={`h-24 rounded-md overflow-hidden cursor-pointer border-2 ${article.image === img ? 'border-indigo-500' : 'border-transparent'}`}
                    onClick={() => selectPlaceholderImage(img)}
                  >
                    <img 
                      src={img} 
                      alt={`Placeholder ${i+1}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <Input
                id="image"
                placeholder="Or enter a custom image URL"
                value={article.image || ""}
                onChange={e => setArticle({ ...article, image: e.target.value })}
              />
              <div className="mt-2 rounded-md overflow-hidden h-32 bg-gray-100">
                <img 
                  src={article.image} 
                  alt="Article preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = placeholderImages[3];
                    setArticle({ ...article, image: placeholderImages[3] });
                  }}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                placeholder="e.g., Technology, Business, Health"
                value={article.category || ""}
                onChange={e => setArticle({ ...article, category: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="readTime">Estimated Read Time</Label>
              <Input
                id="readTime"
                placeholder="e.g., 5 min read"
                value={article.readTime || ""}
                onChange={e => setArticle({ ...article, readTime: e.target.value })}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="premium"
                checked={article.premium || false}
                onCheckedChange={(checked) => 
                  setArticle({ ...article, premium: checked })
                }
              />
              <Label htmlFor="premium">Premium Content (Behind Paywall)</Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="w-full h-10 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ai-indigo"
                value={article.status || "draft"}
                onChange={e => setArticle({ ...article, status: e.target.value })}
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </CardContent>
          
          <CardFooter className="justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin")}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" className="bg-ai-indigo" disabled={saving}>
              {saving ? "Creating..." : "Create Article"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CreateArticleForm;
